import { useNavigate, useLocation } from 'react-router-dom';
import { useLocale } from '../hooks/useLocale.jsx';
import { isLandingPath } from '../utils/landing-paths.js';

// Mobile-only top bar (Room AI 식). 좌상단 ← (페이지 navigation back) +
// 가운데 페이지 제목. 글로벌 — 라우트별로 자동 ← / 제목 결정.
//
// 두 가지 mode:
//   1. 탭 root + 제목 없음 (Feed/Create/My/Account 등) — status bar safe-area
//      만 차지하는 빈 spacer. 컨텐츠가 노치 밑에 안 깔리도록.
//   2. Sub-page (back+title 또는 wordmark) — 평소엔 sticky, 스크롤 다운하면
//      Instagram 식으로 위로 사라짐 / 스크롤 업 하면 부드럽게 내려옴.

// 탭 root 페이지 — 뒤로 갈 곳 없으니 back 버튼 hide.
// MobileTabBar 의 각 탭이 navigate 하는 path 들 + 그 alias 모두 포함.
const TAB_ROOT_PATHS = new Set([
  '/',           // 데스크톱 home (모바일은 /create 로 redirect 됨)
  '/create',     // Create 탭 (단, ?step=style 일 때는 sub-page 처리)
  '/my',         // My 탭 (→ /designs 로 redirect)
  '/designs',    // My 탭 실제 path (no id)
  '/bookmarks',  // My 탭 sub
  '/account',    // Account 탭
  '/gallery',    // 갤러리 탭 (2.3.0)
  '/gio',        // 디자이너(Gio) 탭 (2.3.0)
]);

// 모바일 탭 root 는 제목 없이 비워둠 — 하단 탭바가 위치 알려주고 컨텐츠 공간
// 확보 (Instagram·TikTok·Pinterest 식 content-first 패턴). Sub-page 만 제목.
const TITLE_BY_ROUTE = {
  '/invite': 'inviteTitle',
  '/terms': 'terms',
  '/privacy': 'privacy',
  '/support': 'support',
  '/delete-account': 'deleteAccount',
};

// 화면 안에 제 back 버튼을 가진 라우트 — 헤더가 또 그리면 위가 두 줄이 되고
// auto-hide 중에 서로 겹친다. (Create step 2 는 StepProgress, 디자인 상세는
// .detail-back-btn 이 각각 들고 있다.)
const OWN_BACK_PATTERNS = [/^\/designs\/[^/]+$/, /^\/c\/[^/]+$/, /^\/s\/[^/]+$/];

export function MobileHeader() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useLocale();

  // 마케팅 landing 은 full-bleed — app chrome 모두 숨김.
  if (isLandingPath(pathname)) return null;

  // /create 는 step 과 무관하게 tab root — 즉 여기선 빈 spacer 만 그린다.
  // step 2 의 back 버튼은 StepProgress 줄 안에 있다 (App.jsx). 예전엔 여기서
  // sub-page 헤더로 그렸는데, 그러면 back 이 step 줄과 **별개의 행**을 차지해
  // 위쪽이 두 줄이 되고, auto-hide 로 헤더가 올라가는 동안 back 과 크레딧 칩이
  // 서로 반쯤 겹쳐 잘렸다. 한 줄로 합치는 쪽이 맞다.
  const hasOwnBack = OWN_BACK_PATTERNS.some((re) => re.test(pathname));
  const isTabRoot = TAB_ROOT_PATHS.has(pathname) || hasOwnBack;
  // 데스크톱 home '/' 만 archelier 워드마크. /feed 는 빈 헤더.
  const isHome = pathname === '/';
  const titleKey = TITLE_BY_ROUTE[pathname] || null;
  const showBack = !isTabRoot;
  const showTitle = isHome || titleKey;

  // 빈 탭 root — back 도 제목도 없음. status bar safe-area 만 차지하는 spacer
  // 만 그림 (auto-hide 하지 않음 — OS status bar 가 항상 흰 배경 위에 있게).
  if (!showBack && !showTitle) {
    return <header className="mobile-header mobile-header-empty" aria-hidden="true" />;
  }

  // 디테일 페이지에서 직접 진입 (deep link / 외부 링크) 시 back 못 갈 수 있음 →
  // history 길이 체크해서 fallback 으로 /create (모바일 default landing) 로 이동.
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/create');
  };

  // Instagram 식 auto-hide 는 App.jsx 의 ScrollDirectionBodyClass 가
  // body.scroll-down 클래스로 broadcast — CSS 가 그걸 받아서 transform.
  return (
    <header className="mobile-header" role="navigation">
      {showBack ? (
        <button
          type="button"
          className="mobile-header-back"
          onClick={handleBack}
          aria-label="Back"
        >
          <i className="material-symbols-rounded">arrow_back_ios_new</i>
        </button>
      ) : (
        <span className="mobile-header-side" aria-hidden="true" />
      )}
      <h1 className="mobile-header-title">
        {isHome ? t('appName') : (titleKey ? t(titleKey) : '')}
      </h1>
      <span className="mobile-header-side" aria-hidden="true" />
    </header>
  );
}
